import { CommonModule, NgClass, NgFor } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { VexDateFormatRelativePipe } from '@vex/pipes/vex-date-format-relative/vex-date-format-relative.pipe';
import { CiudUserAuthData } from 'src/app/core/auth/authData.model';
import { LocalStorageDataService } from 'src/app/pages/pages/auth/services/local-storage-data.service';
import { FechaEspanolPipe } from 'src/app/pipes/fecha-espanol.pipe';
import { Notifications } from './interfaces/interfaces.model';
import { NotificationsService } from './services/notifications.service';

@Component({
  selector: 'vex-toolbar-notifications-dropdown',
  templateUrl: './toolbar-notifications-dropdown.component.html',
  styleUrls: ['./toolbar-notifications-dropdown.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgFor,
    MatRippleModule,
    RouterLink,
    NgClass,
    VexDateFormatRelativePipe,
    FechaEspanolPipe,
    CommonModule
  ]
})
export class ToolbarNotificationsDropdownComponent implements OnInit {
  notifications: Notifications[] = []

  isLoading: boolean = true;
  currentUser: CiudUserAuthData | null = null;

  constructor(private notificationsService: NotificationsService, private localStorageDataService: LocalStorageDataService) {}

  ngOnInit() {
    this.currentUser = this.localStorageDataService.getData('user');

    if (this.currentUser?.id_ciud) {
      this.loadNotifications(this.currentUser.id_ciud);
    }
  }

  loadNotifications(userId: number): void {
    this.isLoading = true;
    this.notificationsService.getNotifications(userId).subscribe({
      next: (response: any) => {
        this.isLoading = false;

        // Adaptamos el procesamiento según la estructura de respuesta
        if (response.success && Array.isArray(response.logs)) {
          this.notifications = response.logs.map(
            (log: { id: any; tipo_actividad: any; descripcion: any; fecha_creacion: any; colorClass: any, icon: any, leida: boolean}) =>
              new Notifications({
                id: log.id,
                tipo_actividad: log.tipo_actividad,
                descripcion: log.descripcion,
                fecha_creacion: log.fecha_creacion,
                colorClass: log.colorClass,
                icon: log.icon,
                leida: log.leida,
              })
          );

          // Actualizar el estado de notificaciones sin leer
          const hayNotificacionesSinLeer = this.notifications.some(notification => !notification.leida);
          this.notificationsService.actualizarEstadoNotificacionesSinLeer(hayNotificacionesSinLeer);
        } else if (Array.isArray(response)) {
          // Si la respuesta ya es un array (dependiendo de la estructura de tu API)
          this.notifications = response.map(
            (log: { id: any; tipo_actividad: any; descripcion: any; fecha_creacion: any; colorClass: any, icon: any, leida: boolean}) =>
              new Notifications({
                id: log.id,
                tipo_actividad: log.tipo_actividad,
                descripcion: log.descripcion,
                fecha_creacion: log.fecha_creacion,
                colorClass: log.colorClass,
                icon: log.icon,
                leida: log.leida,
              })
          );

          // Actualizar el estado de notificaciones sin leer
          const hayNotificacionesSinLeer = this.notifications.some(notification => !notification.leida);
          this.notificationsService.actualizarEstadoNotificacionesSinLeer(hayNotificacionesSinLeer);
        } else {
          console.error('Formato inesperado de la respuesta:', response);
        }
      },
      error: (err) => {
        console.error('Error al cargar las notificaciones:', err);
        this.isLoading = false;
      },
    });
  }

  trackById(index: number, item: Notifications): number | undefined {
    return item.id;
  }

  markAsRead(notificationId: number | undefined): void {
    if (notificationId === undefined) {
      console.error('Intento de marcar como leída una notificación sin ID');
      return;
    }

    const index = this.notifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      this.notificationsService.updateNotificationReadState(notificationId, this.currentUser?.id_ciud ?? 0).subscribe({
        next: (response) => {
          this.notifications.splice(index, 1);

          // Actualizar el estado de notificaciones sin leer después de marcar como leída
          const hayNotificacionesSinLeer = this.notifications.some(notification => !notification.leida);
          this.notificationsService.actualizarEstadoNotificacionesSinLeer(hayNotificacionesSinLeer);
        },
        error: (err) => console.error('Error al marcar la notificación:', err)
      });
    } else {
      console.error('Notificación no encontrada con ID:', notificationId);
    }
  }

  markAllAsRead(): void {
    const ids = this.notifications.map(n => n.id);
    this.notificationsService.updateAllNotificationsReadState(this.currentUser?.id_ciud ?? 0).subscribe({
      next: (response) => {
        this.notifications = [];

        // Todas las notificaciones están leídas después de marcar todas como leídas
        this.notificationsService.actualizarEstadoNotificacionesSinLeer(false);
      },
      error: (err) => console.error('Error al marcar todas las notificaciones:', err)
    });
  }
}
